import {Modal} from '@components/ui/modal/Modal.tsx'
import {useRootStore} from '@/store'
import {selectPaidMessageModal} from '@/store/selectors.ts'
import {useTranslation} from 'react-i18next'
import {
    StyledAmountsWrapper,
    StyledPaidMessageModalWrapper,
    StyledParagraph,
    StyledSubtitleModal
} from '@/features/chat/components/AttachmentsPanel/style.ts'
import {TypePaidMessageE} from '@/features/chat/components/AttachmentsPanel/components/SendPaidMessage.tsx'
import {InputText} from '@components/commons/input-text/InputText.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {useMemo, useState} from 'react'
import {CardAmountToSelect, SelectedAmount} from '@components/ui/card-amount-to-select/CardAmountToSelect.tsx'
import {MediaTypeE} from '@/types.ts'
import {useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {StyledTextareaWithBannedElementsTrap} from '@/features/chat/components/AttachmentsPanel/style'
import {HaveBannedElementObj} from '@/features/generated-content-security/types.ts'
import {ChannelID, GroupParams} from '@/features/chat/types.ts'
import {useParams} from 'react-router-dom'
import {useMe} from '@/features/authentication/queries/useMe.ts'
import {getChatChannelUserIds, getCorrectUserId, getFileType} from '@utilities/helpers.tsx'
import {UserV3} from '@/features/authentication/types.ts'
import {UploadAreaPaidMessage} from '@/features/chat/components/AttachmentsPanel/components/UploadAreaPaidMessage.tsx'
import {
    ACCEPT_AUDIO_FILE_TYPES,
    ACCEPT_DOCUMENT_FILE_TYPES,
    ACCEPT_IMAGE_FILE_TYPES,
    ACCEPT_VIDEO_FILE_TYPES,
    MAX_AUDIO_SIZE,
    MAX_FILE_SIZE
} from '@utilities/constants/fileUploader.ts'
import {dropzoneStyles} from '@/features/goal/components/goal-media-uploader/style.ts'
import {FileUploader} from '@components/ui/file-uploader/FileUploader.tsx'
import {FileWithPath} from 'react-dropzone'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import {PAID_MEDIA_AMOUNTS} from '@/featuresConfig'
import {useHandleError} from '@/hooks/useHandleError'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useGetChannelDetailsV3} from '@/features/chat/queries/useGetChannelDetailsV3'
import {useSendChatFileV3} from '@/features/chat/queries/useSendChatFileV3'
import {FileTypeE, ScopeUploadFileE} from '@/features/chat/services/chat.http'
import {useSendMessageV3} from '@/features/chat/queries/useSendMessageV3'
import {MessageTypeE} from '@/features/chat/chat.model'
import {useSendBroadcastFileV3} from '@/features/chat/queries/useSendBroadcastFileV3'
import {HostTooltipSourceE} from '../../ChatFileInput/tooltip-content/HostTooltipContent'
import {useSendGroupFileV3} from '@/features/chat/queries/useSendGroupFileV3'

const amountForm = z.object({
    amount: z.coerce
        .number()
        .int()
        .min(1, {message: 'validation:enter_valid_number'})
        .max(999999, {message: 'validation:paid_message_amount_value'})
        .nullable()
        .optional(),
    message: z.string().min(1, {message: 'validation:proof_message_required'}).nullable().optional()
})
export const SendPaidMessageModal = ({
    onSendMessage = null,
    onCloseLocalModal = null,
    typeLocalModal,
    source = HostTooltipSourceE.CHAT,
    sourceData
}: {
    onSendMessage?: ((params: GroupParams) => void) | null
    onCloseLocalModal?: (() => void) | null
    typeLocalModal?: TypePaidMessageE
    source: HostTooltipSourceE
    sourceData?: {
        usersIds: number[]
        goalsIds: number[]
    }
}) => {
    const {channel} = useParams()
    const {t} = useTranslation()
    const {closeModal: onCloseSharedModal, type: typeSharedModal} = useRootStore(selectPaidMessageModal)
    const {data: user} = useMe()
    const {data: channelDetails} = useGetChannelDetailsV3(channel as ChannelID)
    const [guestId, hostId] = useMemo(() => getChatChannelUserIds(`${channel}`), [channel])
    const mixpanel = useMixPanel()
    const type = typeLocalModal ?? typeSharedModal
    const isFile =
        type === TypePaidMessageE.MEDIA || type === TypePaidMessageE.DOCUMENT || type === TypePaidMessageE.AUDIO

    const [selectedAmount, setSelectedAmount] = useState<SelectedAmount | null>(null)
    const [haveBannedContent, setHaveBannedContent] = useState<HaveBannedElementObj | null>(null)
    const [isSuccessfulUpload, setIsSuccessfulUpload] = useState(false)
    const [file, setFile] = useState<File | FileWithPath | null>(null)

    const userID = getCorrectUserId(user as UserV3)
    const [isLoading, setIsLoading] = useState(false)

    const {
        mutateAsync: sendChatFile,
        isError: isSendFileError,
        error: sendFileError,
        isPending: isPendingSendFile
    } = useSendChatFileV3(String(channel))
    const {
        mutateAsync: sendBroadcastFile,
        isError: isSendBroadcastFile,
        error: sendBroadcastFileError,
        isPending: isPendingSendBroadcastFile
    } = useSendBroadcastFileV3(sourceData)

    const {
        mutateAsync: sendGroupFile,
        isError: isSendGroupFile,
        error: sendGroupFileError,
        isPending: isPendingSendGroupFile
    } = useSendGroupFileV3()

    const downloadHandler = {
        [HostTooltipSourceE.CHAT]: sendChatFile,
        [HostTooltipSourceE.BROADCAST]: sendBroadcastFile,
        [HostTooltipSourceE.GROUPS]: sendGroupFile
    }[source]

    const {
        mutateAsync: sendMessage,
        isError: isSendMessageError,
        error: sendMessageError,
        isPending: isPendingSendMessage
    } = useSendMessageV3({chatIdentifier: String(channel)})
    const isSendingMessage =
        isLoading || isPendingSendFile || isPendingSendMessage || isPendingSendBroadcastFile || isPendingSendGroupFile

    const combinedErrorState = {
        isError: isSendFileError || isSendMessageError || isSendBroadcastFile || isSendGroupFile,
        error: sendFileError || sendMessageError || sendBroadcastFileError || sendGroupFileError
    }

    useHandleError(combinedErrorState)

    const {
        register,
        control,
        setValue,
        clearErrors,
        formState: {errors}
    } = useForm<z.infer<typeof amountForm>>({
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: zodResolver(amountForm),
        defaultValues: {
            amount: null,
            message: null
        }
    })

    const amountWatch = useWatch({
        control: control,
        name: 'amount'
    })

    const messageWatch = useWatch({
        control: control,
        name: 'message'
    })

    const onAmountClick = (amount: SelectedAmount) => {
        setSelectedAmount(amount)
        setValue('amount', null)
        if (errors['amount']?.message) {
            clearErrors('amount')
        }
    }

    const errorMessages = useMemo(() => {
        return errors?.message?.message
    }, [errors])

    const isButtonDisabled = useMemo(() => {
        const isAmountSelected = !!selectedAmount?.amount || !!amountWatch
        const isFileSelected = !!file
        if (isFile) {
            return !(isAmountSelected && isFileSelected)
        } else {
            return !(isAmountSelected && messageWatch)
        }
    }, [selectedAmount, amountWatch, isFile, messageWatch, file])

    const closeModal = () => {
        onCloseLocalModal ? onCloseLocalModal() : onCloseSharedModal()
    }

    const handleSendPaidMediaMessage = async () => {
        if (haveBannedContent?.isBanned) return null
        setIsLoading(true)
        const fileType = file ? getFileType(file.type) : ''
        const fileName = `${file?.name}`
        const fileContentType = file?.type || ''
        if (isFile) {
            if (onSendMessage && onCloseLocalModal && source === HostTooltipSourceE.CHAT) {
                const params = {
                    media: file || undefined,
                    type: fileType,
                    morgi: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0,
                    message: undefined
                }

                onSendMessage(params as GroupParams)
            } else {
                !!fileType &&
                    (await downloadHandler({
                        payload: {
                            fileName,
                            fileContentType,
                            fileType: fileType as FileTypeE,
                            scope: ScopeUploadFileE.CHAT_ATTACHMENT
                        },
                        file: file as File,
                        senderId: +hostId,
                        receiverId: +guestId,
                        metadata: {
                            morgiAmount:
                                (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
                        }
                    }))

                mixpanel?.trackEvent(MixpanelEventNameE.UncoverContentSentHostSide, {
                    file_type: `${fileType}`,
                    guest_id: guestId,
                    morgi_amount: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
                })
                mixpanel?.trackEvent(MixpanelEventNameE.UncoverContentSentGuestSide, {
                    file_type: `${fileType}`,
                    host_id: `${userID}`,
                    morgi_amount: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
                })
            }
        } else {
            if (onSendMessage && onCloseLocalModal) {
                const params: GroupParams = {
                    message: messageWatch,
                    user_id: user?.id,
                    type: MessageTypeE.TEXT,
                    metadata: {
                        rookie: {full_name: user?.fullName},
                        message: messageWatch
                    },
                    morgi: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
                }

                onSendMessage(params)
            } else {
                await sendMessage({
                    message: {
                        type: MessageTypeE.TEXT,
                        senderId: +hostId,
                        receiverId: +guestId,
                        metadata: {
                            morgiAmount:
                                (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
                        },
                        text: String(messageWatch)
                    }
                })
            }

            mixpanel?.trackEvent(MixpanelEventNameE.UncoverContentSentHostSide, {
                file_type: 'paid_text_message',
                guest_id: guestId,
                morgi_amount: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
            })
            mixpanel?.trackEvent(MixpanelEventNameE.UncoverContentSentGuestSide, {
                file_type: `${fileType}`,
                host_id: `${userID}`,
                morgi_amount: (selectedAmount?.amount ? +selectedAmount?.amount : +(amountWatch as number)) || 0
            })
        }
        setIsLoading(false)
        closeModal()
    }

    const sendFile = ({file}: {file: File}) => {
        setFile(file)
        setIsSuccessfulUpload(true)
    }

    const onRemove = () => {
        setFile(null)
        setIsSuccessfulUpload(false)
    }

    const content = useMemo(() => {
        switch (type) {
            case TypePaidMessageE.AUDIO:
                return {
                    title: 'paid_media_modal:audio_title',
                    subtitle: 'paid_media_modal:audio_subtitle',
                    acceptFileType: {...ACCEPT_AUDIO_FILE_TYPES},
                    description: 'paid_media_modal:description'
                }
            case TypePaidMessageE.MEDIA:
                return {
                    title: 'paid_media_modal:title',
                    subtitle: 'paid_media_modal:upload_file',
                    acceptFileType: {...ACCEPT_VIDEO_FILE_TYPES, ...ACCEPT_IMAGE_FILE_TYPES},
                    description: 'paid_media_modal:description'
                }
            case TypePaidMessageE.TEXT:
                return {
                    title: 'chat:pay_for_message:modal_title',
                    subtitle: 'chat:pay_for_message:modal_description',
                    description: 'paid_media_modal:description'
                }
            case TypePaidMessageE.DOCUMENT:
            default:
                return {
                    title: 'paid_media_modal:title_document',
                    subtitle: 'paid_media_modal:upload_file',
                    acceptFileType: ACCEPT_DOCUMENT_FILE_TYPES,
                    description: 'paid_media_modal:description_document'
                }
        }
    }, [type])

    return (
        <Modal
            onClose={closeModal}
            title={t(content.title)}
            maxHeight="600px"
            height="90%"
            minHeight="460px"
            width="90vw"
            maxWidth="400px"
        >
            {isSendingMessage && <Spinner />}

            <StyledPaidMessageModalWrapper direction={'column'} align={'center'} gap={2} padding={2}>
                <StyledSubtitleModal>{t(content.subtitle)}</StyledSubtitleModal>

                {isFile ? (
                    <FileUploader
                        uploadAreaProps={{type}}
                        UploadArea={UploadAreaPaidMessage}
                        uploadFunction={sendFile}
                        accept={content.acceptFileType}
                        onRemove={onRemove}
                        dropzoneStyles={dropzoneStyles}
                        isError={false}
                        isSuccessfulUpload={isSuccessfulUpload}
                        uploadedFile={file}
                        maxSize={type === TypePaidMessageE.AUDIO ? MAX_AUDIO_SIZE : MAX_FILE_SIZE}
                    />
                ) : (
                    <StyledTextareaWithBannedElementsTrap
                        placeholder={t('chat:pay_for_message:placeholder')}
                        onChange={value => setValue('message', value)}
                        ref={register('message').ref}
                        errorMessage={errorMessages}
                        inModal
                        getHaveBannedElements={setHaveBannedContent}
                    />
                )}

                <StyledSubtitleModal>{t('paid_media_modal:choose_amount')}</StyledSubtitleModal>

                <StyledAmountsWrapper>
                    {PAID_MEDIA_AMOUNTS.map(amount => (
                        <CardAmountToSelect
                            key={amount.id}
                            amount={amount}
                            selected={selectedAmount?.id === amount.id}
                            onClick={onAmountClick}
                        />
                    ))}
                </StyledAmountsWrapper>

                <StyledSubtitleModal>{t('pay_at_first_connection:other')}</StyledSubtitleModal>

                <InputText
                    type={'number'}
                    placeholder={t('pay_at_first_connection:choose')}
                    {...register('amount')}
                    minLength={1}
                    onClick={() => setSelectedAmount(null)}
                    errorMessage={`${t(errors['amount']?.message || '')}`}
                />

                {(selectedAmount || amountWatch) && isFile && (
                    <StyledParagraph>
                        {t(`${content.description}`, {
                            FULE_NAME: channelDetails?.user?.fullName,
                            AMOUNT: selectedAmount?.amount ?? amountWatch,
                            FILE_TYPE: isFile ? MediaTypeE.IMAGE : TypePaidMessageE.TEXT
                        })}
                    </StyledParagraph>
                )}

                <Divider />

                <Button
                    disabled={isButtonDisabled || isSendingMessage || haveBannedContent?.isBanned}
                    onClick={handleSendPaidMediaMessage}
                >
                    {t('paid_media_modal:button')}
                </Button>
            </StyledPaidMessageModalWrapper>
        </Modal>
    )
}
